import { useState, useEffect } from "react";

/**
 * UserPreferencesModal
 *
 * Modal overlay for viewing/editing user preferences.
 * Fetches existing preferences on open and saves changes to MySQL.
 *
 * Props:
 *   isOpen    - boolean to show/hide
 *   onClose   - callback to close the modal
 */
export default function UserPreferencesModal({ isOpen, onClose }) {
  const [level, setLevel] = useState("");
  const [showTips, setShowTips] = useState("");
  const [hero, setHero] = useState("");
  const [industry, setIndustry] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState("");

  const userEmail = localStorage.getItem("userEmail");
  const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");

  // Fetch existing preferences when modal opens
  useEffect(() => {
    if (!isOpen || !userEmail) return;

    setFetching(true);
    setError("");

    const fetchPreferences = async () => {
      try {
        const res = await fetch(`${API_URL}/user/preferences?email=${encodeURIComponent(userEmail)}`);
        const data = await res.json();

        if (data.preferences && Object.keys(data.preferences).length > 0) {
          const prefs = data.preferences;
          setLevel(prefs.level || "");
          setShowTips(prefs.show_tips || "");
          setHero(prefs.hero || "");
          setIndustry(prefs.industry || "");
        }
      } catch (err) {
        console.error("Failed to fetch preferences:", err);
      } finally {
        setFetching(false);
      }
    };

    fetchPreferences();
  }, [isOpen, userEmail, API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!level || !showTips) {
      setError("Please answer all required questions");
      return;
    }

    setLoading(true);

    try {
      const preferences = {
        level: level,
        show_tips: showTips,
        hero: hero || "",
        industry: industry || "",
      };

      const res = await fetch(`${API_URL}/user/preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: userEmail,
          preferences: preferences,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to save preferences");
      }

      onClose();
    } catch (err) {
      setError(err.message || "Failed to save preferences");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4 border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 sticky top-0 bg-gray-800 z-10 rounded-t-xl">
          <div>
            <h2 className="text-xl font-bold text-gray-100">Your Preferences</h2>
            <p className="text-gray-500 text-xs mt-1">Update your preferences below</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-200 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {fetching ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-900 border border-red-700 rounded p-3 text-sm text-red-100 mb-6">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Investment Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    How much do you know about investing? <span className="text-red-400">*</span>
                  </label>
                  <div className="space-y-2">
                    {["Novice", "Intermediate", "Expert"].map((option) => (
                      <label
                        key={option}
                        className="flex items-center p-3 rounded bg-gray-700 hover:bg-gray-600 cursor-pointer transition-colors"
                      >
                        <input
                          type="radio"
                          name="level"
                          value={option.toLowerCase()}
                          checked={level === option.toLowerCase()}
                          onChange={(e) => setLevel(e.target.value)}
                          className="mr-3"
                        />
                        <span className="text-gray-200">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Show Tips */}
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Would you like us to show you some helpful tips on financial data and ratios?{" "}
                    <span className="text-red-400">*</span>
                  </label>
                  <div className="space-y-2">
                    {["Yes", "No"].map((option) => (
                      <label
                        key={option}
                        className="flex items-center p-3 rounded bg-gray-700 hover:bg-gray-600 cursor-pointer transition-colors"
                      >
                        <input
                          type="radio"
                          name="showTips"
                          value={option.toLowerCase()}
                          checked={showTips === option.toLowerCase()}
                          onChange={(e) => setShowTips(e.target.value)}
                          className="mr-3"
                        />
                        <span className="text-gray-200">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Investing Heroes */}
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    We are religious followers of Buffet and Munger, tell us who your investing heroes are?
                  </label>
                  <textarea
                    value={hero}
                    onChange={(e) => setHero(e.target.value)}
                    placeholder="e.g., Warren Buffett, Charlie Munger, Peter Lynch..."
                    className="w-full p-3 rounded bg-gray-700 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    rows={3}
                  />
                </div>

                {/* Industry/Edge */}
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Which industry do you work in? (Your edge)
                  </label>
                  <input
                    type="text"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    placeholder="e.g., Technology, Healthcare, Finance..."
                    className="w-full p-3 rounded bg-gray-700 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-300 p-3 rounded font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white p-3 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
